import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { IoWarningOutline } from "react-icons/io5";
import { LinearProgress, Typography } from "@mui/material";
import FormAccordion from "../../../components/Form/FormAccordion";
import { FileGroup, SelectFieldStatus, TextAreaField } from "../../../components/Form/FormComponents";
import ActionSideBar from "../partials/ActionSideBar";
import {
	Guarantor_1,
	Guarantor_2,
	LeaseInformation,
	LeaseeInformation,
	LeasorInformation,
} from "../partials/FormFields";
import { StyledForm } from "../../../components/Form/FormComponents.styled";

// eslint-disable-next-line no-unused-vars
const OngoingLeaseForm = ({ leaseData, setLeaseData, updateLeaseObject }) => {
	const [formData, setFormData] = useState(leaseData);
	const [action, setAction] = useState("view");
	const isEditable = action === "edit";
	const [ongoingProgress, setOngoingProgress] = useState(0);

	useEffect(() => {
		// Calculate the progress of the form
		let progress = 65; // Total progress 65 + 35 = 100
		if (formData?.userId?.idCard) progress += 5;
		if (formData?.userId?.utilityBill) progress += 5;
		if (formData?.autoDebit) progress += 5;
		if (formData?.insurance) progress += 5;
		if (formData?.guarantor?.name1) progress += 5;
		if (formData?.guarantor?.name2) progress += 5;
		if (formData?.creditAssessment) progress += 5;

		setOngoingProgress(progress);
	}, []);

	const handleInputChange = (e) => {
		setFormData({ ...formData, [e.target.name]: e.target.value });
	};

	const handleCancel = () => {
		setFormData(leaseData);
		setAction("view");
	};

	const handleSubmit = (e) => {
		e.preventDefault();
		// console.log(formData);
		setLeaseData(formData);
		updateLeaseObject(formData);
	};

	return (
		<>
			<StyledForm onSubmit={handleSubmit}>
				<ActionSideBar
					leaseData={formData}
					action={action}
					setAction={setAction}
					handleCancel={handleCancel}
				/>

				<div className='RightSection'>
					<div className='headerActions'>
						<div className='progress'>
							<Typography variant='p' component='p' pb={1}>
								This deal is {ongoingProgress}% complete.
							</Typography>
							<LinearProgress
								variant='determinate'
								color='success'
								value={ongoingProgress}
								sx={{ height: "6px", borderRadius: "8px" }}
							/>
						</div>

						{ongoingProgress < 100 && (
							<Typography variant='p' color='error' className='flex' sx={{ fontWeight: 500 }}>
								<IoWarningOutline style={{ marginRight: "10px", fontSize: "18px" }} />
								Details marked red are not yet completed.
							</Typography>
						)}
					</div>

					<FormAccordion title='Lease Information' open={true}>
						<div className='grid grid-cols-3 gap-3'>
							<LeaseInformation
								formData={formData}
								isEditable={isEditable}
								handleInputChange={handleInputChange}
								page='ongoing'
							/>
						</div>
					</FormAccordion>

					<FormAccordion title='Product description' open={true}>
						<div className='form-group'>
							<TextAreaField
								name='product_desc'
								value={formData?.description || formData?.productId.description}
							/>
						</div>
					</FormAccordion>

					<FormAccordion title='Key features'>
						<div className='form-group'>
							<TextAreaField
								name='product_features'
								value={formData?.keyFeatures && formData?.keyFeatures?.replace(/;/g, "<br/>")}
							/>
						</div>
					</FormAccordion>

					<FormAccordion title='Product specifications'>
						<div className='form-group'>
							<TextAreaField
								name='product_specs'
								value={formData?.specifications && formData?.specifications?.replace(/;/g, "<br/>")}
							/>
						</div>
					</FormAccordion>

					<FormAccordion title='Leasor’s Information'>
						<LeasorInformation formData={formData} />
					</FormAccordion>

					<FormAccordion title='Leasee’s Information'>
						<LeaseeInformation formData={formData} />
					</FormAccordion>

					<FormAccordion title='ID card' open={formData?.userId?.idCard ? false : true}>
						{!formData?.userId?.idCard ? (
							<div className='form-group'>
								<SelectFieldStatus value={"Pending"} type={"error"} />
							</div>
						) : (
							<div className='form-group'>
								<FileGroup name='idCard' value={formData?.userId?.idCard} />
							</div>
						)}
					</FormAccordion>

					<FormAccordion title='Utility bill' open={formData?.userId?.utilityBill ? false : true}>
						{!formData?.userId?.utilityBill ? (
							<div className='form-group'>
								<SelectFieldStatus value={"Pending"} type={"error"} />
							</div>
						) : (
							<div className='form-group'>
								<FileGroup name='utilityBill' value={formData?.userId?.utilityBill} />
							</div>
						)}
					</FormAccordion>

					<FormAccordion title='Auto debit' open={formData?.autoDebit ? false : true}>
						{!formData?.autoDebit ? (
							<div className='form-group'>
								<SelectFieldStatus value={"Pending"} type={"error"} />
							</div>
						) : (
							<div className='grid grid-cols-3 gap-3'>
								<div className='form-group'>
									<SelectFieldStatus name='autoDebit' value={formData?.autoDebit} type={"success"} />
								</div>
							</div>
						)}
					</FormAccordion>

					<FormAccordion title='Insurance' open={formData?.insurance ? false : true}>
						{!formData?.insurance ? (
							<div className='form-group'>
								<SelectFieldStatus value={"Pending"} type={"error"} />
							</div>
						) : (
							<div className='grid grid-cols-3 gap-3'>
								<div className='form-group'>
									<SelectFieldStatus
										name='insurance'
										value={formData?.insurance}
										type={formData.insurance?.toLowerCase}
									/>
								</div>
							</div>
						)}
					</FormAccordion>

					<FormAccordion title='Guarantor 1' open={formData?.guarantor?.name1 ? false : true}>
						{!formData?.guarantor?.name1 ? (
							<div className='form-group'>
								<SelectFieldStatus value={"Pending"} type={"error"} />
							</div>
						) : (
							<Guarantor_1 formData={formData} />
						)}
					</FormAccordion>

					<FormAccordion title='Guarantor 2' open={formData?.guarantor?.name2 ? false : true}>
						{!formData?.guarantor?.name2 ? (
							<div className='form-group'>
								<SelectFieldStatus value={"Pending"} type={"error"} />
							</div>
						) : (
							<Guarantor_2 formData={formData} />
						)}
					</FormAccordion>

					<FormAccordion title='Credit assessment' open={formData?.creditAssessment ? false : true}>
						{!formData?.creditAssessment ? (
							<div className='form-group'>
								<SelectFieldStatus value={"Pending"} type={"error"} />
							</div>
						) : (
							<div className='grid grid-cols-3 gap-3'>
								<div className='form-group'>
									<SelectFieldStatus
										name='creditAssessment'
										handleChange={handleInputChange}
										isEditable={isEditable}
										value={formData?.creditAssessment}
										type={"success"}
									/>
								</div>
							</div>
						)}
					</FormAccordion>
				</div>
			</StyledForm>
		</>
	);
};

// Props Validation
OngoingLeaseForm.propTypes = {
	leaseData: PropTypes.object,
	setLeaseData: PropTypes.func,
	updateLeaseObject: PropTypes.func,
};

export default OngoingLeaseForm;
